/**
 * File Upload Component
 */

import { IBuilderComponent } from '../../base'
import { formatFromVariableText } from '../../format-utils'
import type { VariableTextValue } from '../../types'
import { deepMerge, parseToVariableText } from '../../parser-utils'
import fileTemplate from './file.template.json'

export interface IFileComponentSettings {
  // Data settings
  'file-upload-data-value': string // Column ID
  'file-upload-data-label': VariableTextValue

  // Behavior settings
  'file-upload-allowed-file-types': string[]
  'file-upload-behavior-max-size': {
    unit: string
    value: number
  }
  'file-upload-behavior-show-help-text': boolean
  'file-upload-behavior-required'?: boolean
}

export interface IFileComponent extends IBuilderComponent {
  componentId: 'file'
  groupId: 'form'
  settings: IFileComponentSettings
}

/**
 * File Upload AI Input
 */
export interface AiFileInput {
  columnId: string // Required: maps to table column
  label?: string[] // Format: ["text:Upload File"] or ["text:Select ", "cr:fileType"]
  maxSize?: number // Maximum size in MB
  allowedTypes?: string[] // Allowed file types
  required?: boolean // Is this field required?
}

const FILE_DEFAULT_TEMPLATE = fileTemplate as unknown as IFileComponentSettings

export class FileComp {
  getTemplate(): IFileComponentSettings {
    return FILE_DEFAULT_TEMPLATE
  }

  /**
   * format file component settings to AI file input
   */
  format(settings: IFileComponentSettings): AiFileInput {
    const result: AiFileInput = {
      columnId: settings['file-upload-data-value'],
    }

    // Label
    const label = formatFromVariableText(settings['file-upload-data-label'])
    if (label && label.length > 0) {
      result.label = label
    }

    // Max Size (convert to MB)
    if (settings['file-upload-behavior-max-size']) {
      const maxSize = settings['file-upload-behavior-max-size']
      let sizeInMB = maxSize.value
      if (maxSize.unit === 'gb') {
        sizeInMB = maxSize.value * 1024
      } else if (maxSize.unit === 'kb') {
        sizeInMB = maxSize.value / 1024
      }
      result.maxSize = sizeInMB
    }

    // Allowed Types
    if (settings['file-upload-allowed-file-types'] && settings['file-upload-allowed-file-types'].length > 0) {
      result.allowedTypes = settings['file-upload-allowed-file-types']
    }

    // Required
    if (settings['file-upload-behavior-required'] !== undefined) {
      result.required = settings['file-upload-behavior-required']
    }

    return result
  }

  /**
   * parse AI file input to file component settings
   */
  parse(input: AiFileInput): IFileComponentSettings {
    const result = deepMerge(FILE_DEFAULT_TEMPLATE, {})

    // Data - Column ID (required for form fields)
    if (input.columnId) {
      result['file-upload-data-value'] = input.columnId
    }

    // Data - Label
    if (input.label) {
      result['file-upload-data-label'] = parseToVariableText(input.label)
    }

    // Behavior - Max Size
    if (input.maxSize !== undefined) {
      result['file-upload-behavior-max-size'] = {
        unit: 'mb',
        value: input.maxSize,
      }
    }

    // Behavior - Allowed File Types
    if (input.allowedTypes) {
      result['file-upload-allowed-file-types'] = input.allowedTypes
    }

    return result
  }
}

